import {
  BrowserRouter as Router,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { useEffect, useState } from "react";
import { DateTime } from "luxon";
import { renderRoutes } from "@/utils/helper/renderRoutes";
import { initGA, trackPageView } from "@/analytics/google";
import { initFacebookPixel } from "@/analytics/facebook";

// Layout & UI
import Header from "@/components/header/header";
import Footer from "@/components/footer/footer";
import MobileFooter from "@/components/footer/mobile-footer";
import Preloader from "@/components/preloader/preloader";
import CookieConsentBanner from "@/components/cookies/banner";
import NewsletterPopup from "@/components/pages/newsletter-popup";
// import Popup from "@/components/pages/popup";
import { Toaster } from "@/components/ui/sonner";
import ErrorBoundary from "@/components/error-boundary/ErrorBoundary";

// Redux
import { Provider } from "react-redux";
import { store } from "@/store";

// Context
import { AdminAuthProvider } from "@/context/Admin";
import { AuthProvider } from "@/context/Auth";

// Routes config
import { appRoutes } from "@/routes";
import ScrollToTop from "./components/pages/scroll-to-top";
import ScrollOnNavigate from "./components/pages/scroll-on-navigate";
// import Chatbot from "./components/chatbot/chatbot";

function AppWrapper() {
  const location = useLocation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [isLaunched, setIsLaunched] = useState(false);
  const [isAdModalOpen, setIsAdModalOpen] = useState(false);

  const LAUNCH_DATE = DateTime.fromObject(
    { year: 2025, month: 10, day: 10, hour: 0, minute: 0 },
    { zone: "Africa/Lagos" }
  );

  useEffect(() => {
    const preferences = JSON.parse(localStorage.getItem("cookie_preferences"));

    if (preferences?.analytics) {
      initGA();
      trackPageView(location.pathname + location.search);
    }

    if (preferences?.marketing) {
      initFacebookPixel();
    }
  }, []);

  useEffect(() => {
    const preferences = JSON.parse(localStorage.getItem("cookie_preferences"));

    if (preferences?.analytics) {
      trackPageView(location.pathname + location.search);
    }
  }, [location]);

  useEffect(() => {
    const timer = setTimeout(() => setIsAdModalOpen(true), 3000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 3000);

    const checkLaunch = () => {
      const now = new Date();
      const launched = now >= LAUNCH_DATE;

      setIsLaunched((prev) => {
        if (!prev && launched) {
          setTimeout(() => {
            // Preserve search params and hash when navigating
            navigate(location.pathname + location.search + location.hash, { replace: true });
          }, 0);
        }
        return launched;
      });
    };

    checkLaunch();
    const interval = setInterval(checkLaunch, 1000);

    return () => {
      clearTimeout(timer);
      clearInterval(interval);
    };
  }, [location.pathname, navigate]);

  if (loading) return <Preloader />;

  const hideLayout =
    location.pathname.startsWith("/auth") ||
    location.pathname.startsWith("/dashboard") ||
    location.pathname.startsWith("/admin") ||
    location.pathname === "/setup-password" ||
    location.pathname === "/maintenance" ||
    !isLaunched;

  const handleShopNow = () => (window.location.href = "/shop");
  const handleLearnMore = () =>
    document.getElementById("featured-products")?.scrollIntoView({
      behavior: "smooth",
    });

  return (
    <>
      <ScrollOnNavigate />
      {!hideLayout && <Header />}

      <Routes>{renderRoutes(appRoutes(isLaunched))}</Routes>

      {!hideLayout && (
        <>
          <Footer />
          <MobileFooter />
          <CookieConsentBanner />
          <ScrollToTop />
          {/* <Chatbot /> */}
          {/* <Popup
            isOpen={isAdModalOpen}
            onClose={() => setIsAdModalOpen(false)}
            title="Transform Your Skin Today!"
            subtitle="Discover our premium organic skincare collection"
            description="Limited time offer: Get 25% off your first order plus free shipping on orders over $75."
            imageUrl="/luxury-skincare-products-with-natural-ingredients.jpg"
            primaryButtonText="Shop Collection"
            secondaryButtonText="View Products"
            onPrimaryAction={handleShopNow}
            onSecondaryAction={handleLearnMore}
            offerPercentage="25%"
          /> */}
          {location.pathname !== "/about-us" && <NewsletterPopup />}
        </>
      )}
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <Provider store={store}>
        <AdminAuthProvider>
          <Router>
            <AuthProvider>
              <Toaster />
              <AppWrapper />
            </AuthProvider>
          </Router>
        </AdminAuthProvider>
      </Provider>
    </ErrorBoundary>
  );
}

export default App;
